import { type ReactNode } from "react";

import { isTruthy } from "@vega-core/utils/isTruthy";

import { DotVariant, Tag, type TagProps } from "../Tag/Tag";

export const Status = {
  DONE: "DONE",
  ERROR: "ERROR",
  APPROVED: "APPROVED",
  CANCELLED: "CANCELLED",
  CHANGES_REQUESTED: "CHANGES_REQUESTED",
  CLOSED: "CLOSED",
  COMPLETED: "COMPLETED",
  CONDITIONALLY_APPROVED: "CONDITIONALLY_APPROVED",
  CONVERTED: "CONVERTED",
  CREATED: "CREATED",
  IN_PROGRESS: "IN_PROGRESS",
  IN_REVIEW: "IN_REVIEW",
  IN_SIGNING: "IN_SIGNING",
  NEW: "NEW",
  NOT_APPLICABLE: "NOT_APPLICABLE",
  NOT_STARTED: "NOT_STARTED",
  OPEN: "OPEN",
  PARTIALLY_CONVERTED: "PARTIALLY_CONVERTED",
  PENDING_APPROVAL: "PENDING_APPROVAL",
  PENDING: "PENDING",
  READY_FOR_CONVERSION: "READY_FOR_CONVERSION",
  READY_FOR_PARTIAL_CONVERSION: "READY_FOR_PARTIAL_CONVERSION",
  REJECTED: "REJECTED",
  REOPENED: "REOPENED",
  REQUESTED: "REQUESTED",
  REQUESTED_CHANGES: "REQUESTED_CHANGES",
  REQUESTED_SIGNATURE: "REQUESTED_SIGNATURE",
  RESOLVED: "RESOLVED",
  RETURNED: "RETURNED",
  SUBMITTED: "SUBMITTED",
  LOW: "LOW",
  MEDIUM: "MEDIUM",
  HIGH: "HIGH",
} as const;

export type Status = (typeof Status)[keyof typeof Status];

type Props = Omit<TagProps, "children"> & {
  status?: Status;
  children?: ReactNode;
  showDot?: boolean;
};

const getStatusStyles = (status: Status) => {
  switch (status) {
    case "IN_PROGRESS":
    case "MEDIUM":
      return "Warning";
    case "NOT_STARTED":
    case "CREATED":
    case "NEW":
    case "OPEN":
      return "Data1";
    case "PENDING":
    case "PENDING_APPROVAL":
    case "IN_REVIEW":
    case "IN_SIGNING":
      return "Pending";
    case "READY_FOR_CONVERSION":
    case "READY_FOR_PARTIAL_CONVERSION":
    case "COMPLETED":
    case "DONE":
    case "APPROVED":
    case "CONDITIONALLY_APPROVED":
    case "RESOLVED":
    case "LOW":
      return "Positive";
    case "REOPENED":
    case "CHANGES_REQUESTED":
    case "REQUESTED_SIGNATURE":
    case "SUBMITTED":
    case "REQUESTED":
      return "Data2";
    case "RETURNED":
      return "Data3";
    case "PARTIALLY_CONVERTED":
    case "REQUESTED_CHANGES":
      return "Data4";
    case "REJECTED":
    case "HIGH":
    case "ERROR":
      return "Negative";
    case "CONVERTED":
    case "CANCELLED":
    case "CLOSED":
    case "NOT_APPLICABLE":
      return "Disabled";
    default:
      return "Default";
  }
};

export const formatStatus = (status?: Status): string => {
  if (!status) return "";

  if (status === Status.NOT_APPLICABLE) {
    return "N/A";
  }

  if (status.includes("_")) {
    return status
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  return status.charAt(0) + status.slice(1).toLowerCase();
};

export const StatusTag = ({
  status,
  children,
  isLoading = false,
  showDot = false,
  ...props
}: Props) => {
  const getDotStatus = (status?: Status) => {
    switch (status) {
      case "CONVERTED":
        return DotVariant.CHECK;
      case "CANCELLED":
      case "REJECTED":
        return DotVariant.REMOVED;
      default:
        return DotVariant.DEFAULT;
    }
  };

  return (
    <Tag
      {...props}
      dotVariant={showDot ? getDotStatus(status) : undefined}
      isLoading={isLoading}
      variant={status ? getStatusStyles(status) : "Default"}
    >
      {isTruthy(children) ? children : formatStatus(status)}
    </Tag>
  );
};
